import React, { useState, useEffect, useCallback } from 'react';
import { Table, Select, Button, Tag, Modal } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/ApiService';
import '../Quiz/quizMain.css';

function buildQuery(params) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            search.append(key, value);
        }
    });
    const query = search.toString();
    return query ? `?${query}` : '';
}

const PRIZE_TYPE_LABELS = {
    beko: 'Beko',
    dtl: 'DTL',
    main_clio: 'Clio',
};

const RECORD_TYPE_LABELS = {
    all: 'Svi',
    regular: 'Regularni',
    reserve: 'Rezervni',
};

const getWinnerGroupKey = (winner) => {
    const drawRoundId = winner?.drawRound?.id || 'no-round';
    const prizeType = winner?.prizeType || 'no-prize';
    return `${drawRoundId}::${prizeType}`;
};

const interleaveRegularAndReserve = (group) => {
    const maxLength = Math.max(group.regular.length, group.reserve.length);
    const ordered = [];

    for (let i = 0; i < maxLength; i += 1) {
        if (group.regular[i]) ordered.push(group.regular[i]);
        if (group.reserve[i]) ordered.push(group.reserve[i]);
    }

    return ordered;
};

const orderWinnersForDisplay = (winners) => {
    const groups = new Map();
    const groupOrder = [];

    winners.forEach((winner) => {
        const key = getWinnerGroupKey(winner);
        if (!groups.has(key)) {
            groups.set(key, { regular: [], reserve: [] });
            groupOrder.push(key);
        }

        const group = groups.get(key);
        if (winner.isReserve) {
            group.reserve.push(winner);
            return;
        }

        group.regular.push(winner);
    });

    return groupOrder.flatMap((key) => interleaveRegularAndReserve(groups.get(key)));
};

const PromotionWinners = () => {
    const navigate = useNavigate();
    const [winners, setWinners] = useState([]);
    const [drawRounds, setDrawRounds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [promotingId, setPromotingId] = useState(null);
    const [confirmWinner, setConfirmWinner] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });

    const [filters, setFilters] = useState({
        drawRoundId: undefined,
        prizeType: undefined,
        recordType: 'all',
    });

    const [appliedFilters, setAppliedFilters] = useState(filters);

    const fetchDrawRounds = useCallback(async () => {
        const [success, error] = await apiService.get('promotion/draw-rounds');

        if (error) return;

        setDrawRounds(success?.data?.drawRounds || []);
    }, []);

    const fetchWinners = useCallback(async (page, limit, currentFilters) => {
        setLoading(true);

        const queryParams = {
            page,
            limit,
            drawRoundId: currentFilters.drawRoundId,
            prizeType: currentFilters.prizeType,
        };

        if (currentFilters.recordType === 'regular') {
            queryParams.isReserve = 'false';
        } else if (currentFilters.recordType === 'reserve') {
            queryParams.isReserve = 'true';
        }

        const query = buildQuery(queryParams);
        const [success, error] = await apiService.get(`promotion/winners${query}`);

        setLoading(false);

        if (error) {
            toast.error('Doslo je do greske, pokusajte malo kasnije');
            return;
        }

        const data = success?.data;
        setWinners(orderWinnersForDisplay(data?.winners || []));
        setPagination({
            page: data?.pagination?.page || page,
            limit: data?.pagination?.limit || limit,
            total: data?.pagination?.total || 0,
        });
    }, []);

    useEffect(() => {
        fetchDrawRounds();
    }, [fetchDrawRounds]);

    useEffect(() => {
        fetchWinners(pagination.page, pagination.limit, appliedFilters);
    }, [appliedFilters, pagination.page, pagination.limit, fetchWinners]);

    const formatPhaseLabel = (round) => {
        if (!round) return '-';
        if (round.type === 'main') return 'Glavna faza';
        return `Faza ${round.roundNumber || '-'} (${dayjs(round.drawAt).format('D MMM YYYY')})`;
    };

    const handlePromote = async () => {
        if (!confirmWinner) return;

        setPromotingId(confirmWinner.id);

        const [success, error] = await apiService.patch(
            `promotion/winners/${confirmWinner.id}/promote`
        );

        setPromotingId(null);
        setConfirmWinner(null);

        if (error) {
            const message = error?.response?.data?.error?.message;
            toast.error(message || 'Doslo je do greske, pokusajte malo kasnije');
            return;
        }

        toast.success('Rezervni dobitnik je promovisan u regularnog');
        if (success?.data?.winner) {
            setWinners((prev) =>
                orderWinnersForDisplay(
                    prev.map((w) => (w.id === success.data.winner.id ? success.data.winner : w))
                )
            );
        } else {
            fetchWinners(pagination.page, pagination.limit, appliedFilters);
        }
    };

    const canPromote = (record) =>
        record.isReserve === true && record.drawRound?.status === 'completed';

    const getWinnerEntryId = (record) =>
        record.entryId ||
        record.entry?.id ||
        record.entry?.entryId ||
        record.entry?.entry?.id ||
        record.promotionEntryId ||
        record.promotionEntry?.id ||
        record.receiptEntryId;

    const resolveEntryIdByPfr = async (record) => {
        if (!record?.pfr) return null;

        const query = buildQuery({
            page: 1,
            limit: 1,
            pfr: record.pfr,
        });

        const [success, error] = await apiService.get(`promotion/entries${query}`);
        if (error) return null;

        return success?.data?.entries?.[0]?.id || null;
    };

    const openEntryDetails = async (record) => {
        let entryId = getWinnerEntryId(record);
        if (!entryId) {
            entryId = await resolveEntryIdByPfr(record);
        }

        if (!entryId) {
            toast.error('Prijava za ovog dobitnika nije pronadjena');
            return;
        }

        navigate(`/promotion/entries/${entryId}`);
    };

    const applyFilters = () => {
        setPagination((prev) => ({ ...prev, page: 1 }));
        setAppliedFilters({ ...filters });
    };

    const handleTableChange = (tablePagination) => {
        setPagination((prev) => ({
            ...prev,
            page: tablePagination.current,
            limit: tablePagination.pageSize,
        }));
    };

    const columns = [
        {
            title: 'PFR',
            dataIndex: 'pfr',
            key: 'pfr',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (text) => text || '-',
        },
        {
            title: 'Telefon',
            dataIndex: 'phone',
            key: 'phone',
            render: (text) => text || '-',
        },
        {
            title: 'Nagrada',
            dataIndex: 'prizeType',
            key: 'prizeType',
            render: (type) => PRIZE_TYPE_LABELS[type] || type,
        },
        {
            title: 'Faza',
            key: 'drawRound',
            render: (_, record) => formatPhaseLabel(record.drawRound),
        },
        {
            title: 'Datum',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text) => (text ? dayjs(text).format('D MMM YYYY HH:mm') : '-'),
        },
        {
            title: 'Tip zapisa',
            key: 'recordType',
            render: (_, record) => (
                <Tag color={record.isReserve ? 'gold' : 'green'}>
                    {record.isReserve ? 'Rezerva' : 'Regular'}
                </Tag>
            ),
        },
        {
            title: 'Akcija',
            key: 'action',
            render: (_, record) =>
                canPromote(record) ? (
                    <Button
                        type="primary"
                        size="small"
                        loading={promotingId === record.id}
                        onClick={(event) => {
                            event.stopPropagation();
                            setConfirmWinner(record);
                        }}
                    >
                        Promovisi
                    </Button>
                ) : null,
        },
    ];

    return (
        <div className="quizMain">
            <div className="title">NI dobitnici</div>

            <div className="filters">
                <div className="holder">
                    <label htmlFor="drawRoundFilter">Faza</label>
                    <Select
                        id="drawRoundFilter"
                        allowClear
                        placeholder="Sve faze"
                        style={{ width: 280 }}
                        value={filters.drawRoundId}
                        onChange={(value) => setFilters((prev) => ({ ...prev, drawRoundId: value }))}
                        options={drawRounds.map((round) => ({
                            value: round.id,
                            label: formatPhaseLabel(round),
                        }))}
                    />
                </div>

                <div className="holder">
                    <label htmlFor="prizeTypeFilter">Tip nagrade</label>
                    <Select
                        id="prizeTypeFilter"
                        allowClear
                        placeholder="Sve nagrade"
                        style={{ width: 160 }}
                        value={filters.prizeType}
                        onChange={(value) => setFilters((prev) => ({ ...prev, prizeType: value }))}
                        options={Object.entries(PRIZE_TYPE_LABELS).map(([value, label]) => ({
                            value,
                            label,
                        }))}
                    />
                </div>

                <div className="holder">
                    <label htmlFor="recordTypeFilter">Tip zapisa</label>
                    <Select
                        id="recordTypeFilter"
                        style={{ width: 160 }}
                        value={filters.recordType}
                        onChange={(value) => setFilters((prev) => ({ ...prev, recordType: value }))}
                        options={Object.entries(RECORD_TYPE_LABELS).map(([value, label]) => ({
                            value,
                            label,
                        }))}
                    />
                </div>

                <div className="holder">
                    <label>&nbsp;</label>
                    <Button type="primary" onClick={applyFilters}>
                        Primeni
                    </Button>
                </div>
            </div>

            <Table
                rowKey="id"
                dataSource={winners}
                columns={columns}
                bordered
                loading={loading}
                rowClassName={(record) =>
                    record.isReserve ? 'promotion-reserve-row' : 'promotion-winner-row'
                }
                onRow={(record) => {
                    const entryId = getWinnerEntryId(record);
                    return {
                        onClick: () => openEntryDetails(record),
                        style: { cursor: entryId ? 'pointer' : 'default' },
                    };
                }}
                pagination={{
                    current: pagination.page,
                    pageSize: pagination.limit,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                }}
                onChange={handleTableChange}
            />

            <Modal
                title="Potvrda promocije"
                open={!!confirmWinner}
                onOk={handlePromote}
                onCancel={() => setConfirmWinner(null)}
                okText="Promovisi"
                cancelText="Otkazi"
                confirmLoading={!!promotingId}
            >
                {confirmWinner && (
                    <>
                        Da li ste sigurni da zelite da promovisete rezervnog dobitnika{' '}
                        <strong>{confirmWinner.pfr}</strong> u regularnog?
                    </>
                )}
            </Modal>
        </div>
    );
};

export default PromotionWinners;
